import {
  Button,
  ButtonSpinner,
  H2,
  InputCheckbox,
} from "@sellar-io/components";
import { DialogContent } from "../../../../../../../../components/modal/Dialog";
import { useFullPageDialogContext } from "../../../../../../../../components/modal/useFullPageDialogContext";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { useState } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
} from "@headlessui/react";
import { Icon } from "../../../../../../../../components";
import { useDebounce } from "use-debounce";
import {
  TwoCreateCompanyCreditLimitQuoteResponseType,
  TwoCompanySearchResponseType,
} from "@sellar-io/api-v2/types";
import { apiV2AssertError } from "../../../../../../../../hooks/api/v2/apiV2Request";
import { Alert } from "../../../../../../../../components/alert/Alert";
import { useCreateTwoCompanyCreditLimitQuote } from "../../../../../../../../hooks/api/v2/two/useCreateTwoCompanyCreditLimitQuote";
import { SellarCreditTwoDialogPageEnum } from "../types/SellarCreditTwoDialogPageEnum";
import { useTwoCompanySearch } from "../../../../../../../../hooks/api/v2/two/useTwoCompanySearch";
import { useSellarCurrentBusiness } from "../../../../../../../../hooks/useSellarCurrentBusiness.shared";
import { useIntercom } from "react-use-intercom";
import { useFloatingLayer } from "@sellar-io/components";

export const SellarCreditCheckSearchCompany = () => {
  const [company, setCompany] = useState<
    TwoCompanySearchResponseType[number] | null
  >(null);

  const intercom = useIntercom();

  const { business } = useSellarCurrentBusiness();

  const { onDismiss, setPage } = useFullPageDialogContext();
  const [country] = useState("GB");
  const [query, setQuery] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string>();

  const [searchQuery] = useDebounce(query, 500);

  const twoCompanySearchQuery = useTwoCompanySearch(
    {
      organization_number: searchQuery,
    },
    {
      enabled: !!searchQuery && searchQuery.length > 2,
    },
  );

  // only include companies with company number
  const companies = (twoCompanySearchQuery.data ?? []).filter(
    (company) => company.companyNumber,
  );

  const noResultsFound =
    searchQuery.length > 2 &&
    !companies.length &&
    !twoCompanySearchQuery.isLoading;

  const disabled = !country || !company || !confirm;

  const creditLimitMutation = useCreateTwoCompanyCreditLimitQuote();

  const submit = () => {
    if (company?.companyNumber)
      creditLimitMutation.mutate(
        {
          twoOrganisationNumber: company.companyNumber,
          retailerId: business?.Retailer?.id ?? 0,
        },
        {
          onSuccess: (
            response: TwoCreateCompanyCreditLimitQuoteResponseType,
          ) => {
            if (response.totalLimit <= 0 || response.status !== "eligible") {
              setPage(SellarCreditTwoDialogPageEnum.NOT_ELIGIBLE);
              return;
            }

            setPage(SellarCreditTwoDialogPageEnum.CREDIT_LIMIT);
          },
          onError: (error) => {
            const errorMessage =
              apiV2AssertError(error)?.response?.data.message;
            setError(errorMessage ?? "An unknown error has occurred");
          },
        },
      );
  };

  const { popover } = useFloatingLayer();

  return (
    <>
      <DialogContent className="flex h-full flex-col items-center text-center">
        <H2 className="mb-4">Search for your company</H2>
        <p className="mb-8">
          Enter your company details to discover your credit limit
        </p>
        {error ? (
          <>
            <Alert
              type="warning"
              title="Cannot retrieve credit limit"
              message={error}
            >
              Contact support for more details
            </Alert>
            <div className="mt-6 flex w-full flex-col gap-2">
              <Button className="w-full" onClick={onDismiss}>
                Finish
              </Button>
              <Button
                color="secondary"
                className="w-full"
                onClick={() => intercom.show()}
              >
                Contact support
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4 flex w-full flex-col gap-2 text-left">
              <InputLabel>Country</InputLabel>
              <InputContainer className="w-full">
                <InputSelect value={country} className="w-full" disabled={true}>
                  <InputOption value={"GB"}>United Kingdom</InputOption>
                </InputSelect>
              </InputContainer>
            </div>
            <div className="mb-2 flex w-full flex-col gap-2 text-left">
              <InputLabel>Company</InputLabel>
              <InputContainer className="relative">
                <Icon icon="MAGNIFY" className="absolute" />
                <Combobox
                  value={company}
                  onChange={setCompany}
                  onClose={() => setQuery("")}
                >
                  <ComboboxInput
                    displayValue={(
                      company: TwoCompanySearchResponseType[number] | null,
                    ) => company?.companyName ?? ""}
                    onChange={(event) => setQuery(event.target.value)}
                    className={"w-full border-none pl-7 outline-hidden"}
                    placeholder="Search by company number"
                  />
                  <ComboboxOptions
                    anchor="bottom"
                    className={`relative ${popover} mt-4 h-[300px] w-[418px] bg-white empty:invisible`}
                  >
                    {companies.map((company) => (
                      <ComboboxOption
                        key={company.companyName}
                        value={company}
                        className="flex w-full items-center gap-2 border-b border-b-zinc-300 p-1 data-focus:bg-zinc-100"
                      >
                        <Icon icon="MAP_MARKER" className="flex-none" />
                        <span className="flex-none font-semibold">
                          {company.companyName}
                        </span>
                      </ComboboxOption>
                    ))}
                  </ComboboxOptions>
                </Combobox>
                {twoCompanySearchQuery.isFetching && (
                  <ButtonSpinner className="absolute right-4 text-black" />
                )}
              </InputContainer>

              <div className="text-zinc-500">
                <span className="font-semibold">Please note: </span>
                <span className="font-normal">
                  Companies established very recently may not yet be eligible
                  for trade credit
                </span>
              </div>

              {noResultsFound && (
                <div className="mb-4 text-zinc-800">
                  <span className="font-semibold">No results found. </span>Two
                  inc. could not find the company associated with the company
                  number, please check the number and try again.
                </div>
              )}
            </div>
            {!!company && (
              <label className="flex cursor-pointer gap-4 rounded-lg border border-indigo-600 bg-indigo-50 p-4 text-left">
                <InputCheckbox
                  variant="circular"
                  checked={confirm}
                  onChange={(event) => setConfirm(event.target.checked)}
                  containerClassName="flex-none"
                />
                <div>
                  <p>
                    I confirm that <b>{company.companyName}</b> is the Company I
                    am associated with and would like to check Sellar Credit
                    eligibility for
                  </p>
                </div>
              </label>
            )}

            <div className="mt-auto flex w-full flex-col gap-2">
              <Button
                disabled={disabled}
                loading={creditLimitMutation.isPending}
                onClick={submit}
                className="w-full"
                type="submit"
              >
                Reveal credit limit
              </Button>
              <Button
                color="transparent"
                className="w-full"
                onClick={(e) => {
                  e.preventDefault();
                  setPage(
                    SellarCreditTwoDialogPageEnum.TWO_BUSINESS_REGISTRATION_APPLICATION,
                  );
                }}
              >
                I'm a sole trader
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </>
  );
};
